import { Component, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { ERPComponent } from 'src/app/erp/erp.component';
import { ERPUserMasterOnboardingRequestDTO } from 'src/app/erp/ERPEntityMapping.interface';
import { UserLoginCreateRequestDTO, UserLoginUpdateRequestDTO } from 'src/app/login/userlogin.interface';
import { UserLoginService } from 'src/app/login/userlogin.service';

@Component({
  selector: 'app-user-credential-modal',
  templateUrl: './user-credential-modal.component.html',
  styleUrls: ['./user-credential-modal.component.css']
})
export class UserCredentialModalComponent {
  private ERP = inject(ERPComponent);
@Input() schoolId!:  number
  @Input() userId!: number;   // Dynamically passed
  User = inject(UserLoginService).User()
  @ViewChild('credentialModal') credentialModal!: TemplateRef<any>;
  modalRef: any;
  isPasswordCreateMode = true;

  userLoginForm = new FormGroup({
    loginId: new FormControl(0),
    userId: new FormControl(0),
    userName: new FormControl('', [Validators.required, Validators.pattern(/^[a-zA-Z][a-zA-Z0-9._-]{3,19}$/)]),
    userPassword: new FormControl('', Validators.required),
  });

  constructor(
    private modalService: NgbModal,
    private userLoginService: UserLoginService,
    private toastr: ToastrService
  ) { }


  initialUserName: string = "";
  userNameValid: boolean | null = null;
  userNameError: string | null = null;

  isCheckingUserName: boolean = false;

  ngOnInit() {
    if (this.userId) {
      this.loadUserLogin(this.userId);
    }
    this.userLoginForm.controls.userName.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {

        if (!value || this.userLoginForm.controls.userName.invalid) {
          this.userNameError = null;
          this.userNameValid = null;
          return;
        }
        if (value) {
          this.validateUserName(value);
        }
      });

  }

  validateUserName(userName: string) {
    if (userName === this.initialUserName) {
      this.userNameError = "";
      this.isCheckingUserName = false;
      this.userNameValid = null;
      return;
    }

    this.isCheckingUserName = true;
    this.userNameValid = null;

    this.userLoginService
      .UserLogin_ValidateUserName({ userName })
      .subscribe({
        next: (res) => {
          this.isCheckingUserName = false;

          if (res.responseCode === 409) {
            this.userNameError = res.responseMsg || "Username is already taken";
            this.userNameValid = false;
          } else {
            this.userNameError = "";
            this.userNameValid = true;
          }
        },
        error: () => {
          this.isCheckingUserName = false;
          this.userNameError = "Error Checking Username Availability.";
          this.userNameValid = false;
        },
      });
  }



  open() {
    this.userLoginForm.reset()
    this.modalRef = this.modalService.open(this.credentialModal, { size: 'md', backdrop: 'static' });
  }

  loadUserLogin(userId: number) {
    this.userLoginService.UserLogin_ReadByUserId({ userId: userId }).subscribe({
      next: (res) => {
        if (res && res.loginId > 0) {
          this.isPasswordCreateMode = false;
          this.initialUserName = res.userName!;
          this.userNameValid = null;

          this.userLoginForm.patchValue({
            loginId: res.loginId,
            userId: res.userId,
            userName: res.userName
          });
        }
      },
      error: () => {
        this.isPasswordCreateMode = true;
        this.initialUserName = "";

        this.userLoginForm.patchValue({
          userId: userId,
          loginId: 0,
          userName: ""
        });
      }
    });
  }

  onSubmit() {
    if (!this.userLoginForm.valid) {
      this.userLoginForm.markAllAsTouched();
      return;
    }

    const formData = this.userLoginForm.value;

    if (this.isPasswordCreateMode) {
      let payload: UserLoginCreateRequestDTO = {
        userId: formData.userId ?? 0,
        userName: formData.userName ?? "",
        userPassword: formData.userPassword ?? "",
        actionUser: this.User.userId.toString()
      }
      this.userLoginService.UserLogin_Create(payload).subscribe({
        next: (res) => {
          this.toastr.success('Password created successfully!');
          const erpUserMasterOnboardingCreateData: ERPUserMasterOnboardingRequestDTO =
          {
            userId: res.userId,
            moduleName: "School",
            moduleId: this.schoolId,
          };

          this.ERP.userMasterOnboardingCreate(
            erpUserMasterOnboardingCreateData
          );
          this.modalRef.close();
        },
        error: () => console.error('Failed to create password.')
      });

    } else {

      let payload: UserLoginUpdateRequestDTO = {
        loginId: formData.loginId ?? 0,
        userId: formData.userId ?? 0,
        userName: formData.userName ?? "",
        userPassword: formData.userPassword ?? "",
        actionUser: this.User.userId.toString(),
        isActive: 1
      }
      this.userLoginService.UserLogin_Update(payload).subscribe({
        next: () => {
          this.toastr.success('Password updated successfully!');
          this.modalRef.close();
        },
        error: () => console.error('Failed to update password.')
      });
    }
  }

}
